import type { ContentPack } from '@ab/content-schema';
import type { Event } from '../events.ts';
import type { RunState } from '../state.ts';

/** What every handler receives besides the state and the command. */
export type Ctx = { readonly content: ContentPack };

/** What every command handler returns: the next state and what happened. */
export type ApplyResult = { readonly state: RunState; readonly events: readonly Event[] };

/** A step of a rule: the state in, the next state and its events out. */
export type Step = (state: RunState) => ApplyResult;

/** Runs `steps` in order, threading the state through and concatenating their events. */
export const chain = (state: RunState, ...steps: readonly Step[]): ApplyResult => {
  let s = state;
  const events: Event[] = [];
  for (const step of steps) {
    const r = step(s);
    s = r.state;
    events.push(...r.events);
  }
  return { state: s, events };
};

/** A result with no change. */
export const none = (state: RunState): ApplyResult => ({ state, events: [] });

/**
 * The signature of every command handler. Declaring handlers with this type
 * lets a handler leave out parameters it does not use.
 */
export type CommandHandler<C> = (ctx: Ctx, state: RunState, command: C) => ApplyResult;
